import { uniq } from 'lodash-es';

import { normalizeType } from './production';
import GraphicsService from '../services/graphics';
import ConduitEventsService from '../services/conduitEventsService';
import { getVisibleEls } from './graphics-utils';

const DefaultFonts = [
  '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  'Roboto',
  'Roboto Condensed',
  'Open Sans',
  'Lato',
  'Raleway',
  'Montserrat',
  'Libre Baskerville',
  'Merriweather',
];

export default {
  /**
   * Send "Producer Toggled Live Item" event to Conduit 
   * @param {string} opts.event - Event to concat to end of event name
   * @param {Object} opts.item - Live item
   * @param {Object} opts.rootState - Root store state
   * @param {Object} [opts.preview] - Whether or not user toggled live for preview
   */
  async toggleLiveEvent(opts = {}) {
    const {
      event,
      item,
      rootState,
      rootGetters,
      preview = false,
    } = opts;

    const {
      user,
      panels,
      screens,
    } = rootState;

    const {
      isStaff,
      connectedScreensCount,
      hasConnectedScreens,
    } = rootGetters;

    const { production } = rootState.production;

    const templatesConvertedFromPsd = [];

    const containsCustomFonts = (elements) => {
      for (let i = 0; i < elements.length; i += 1) {
        const el = elements[i];
        if (el.type === 'text' && !DefaultFonts.includes(el.styles.fontFamily)) {
          return true;
        }

        // RECURSION
        if (el.elements?.length && containsCustomFonts(el.elements)) {
          return true;
        }
      }

      return false;
    };

    let qrCodeUrls = [];
    let elements;
    let apps;
    let hasCustomFont = false;
    const sportRadarElements = [];
    const qrCodeElements = [];

    switch (item.layout) {
      case 'panel': {
        const panel = panels.lookupTable[item._id];
        if (panel) {
          qrCodeUrls = panel.settings?.showQrCode ? [panel.settings?.qrCodeUrl] : [];
        }

        break;
      }

      case 'graphic': {
        const graphic = await GraphicsService.getTemplate(item.template_id);
        const visibleElements = getVisibleEls(graphic.elements, item.fields || {});

        qrCodeUrls = visibleElements.filter((el) => el.type === 'qrcode').map((el) => el.permalink);
        elements = visibleElements.reduce((memo, el) => {
          if (el.type === 'sportradar') {
            sportRadarElements.push({
              id: el.id,
              data: el.data,
            });
          }

          if (el.type === 'qrcode') {
            const qrCodeData = item?.fields?.[el?.data?.provider]?.data;
            if (qrCodeData) {
              qrCodeElements.push({
                id: el.id,
                data: qrCodeData,
              });
            }
          }

          return {
          ...memo,
            [`${el.type}_elements`]: (memo[`${el.type}_elements`] || 0) + 1,
          };
        }, {});

        // Partner Apps (e.g. "fox-foresight", "scorestream", "cue")
        apps = visibleElements.reduce((memo, el) => {
          const { partner } = el;
          if (partner) {
            const key = `${partner}_app_elements`;

            return {
              ...memo,
              [key]: (memo[key] || 0) + 1,
            };
          }

          return memo;
        }, {});

        if (graphic.conversionId) {
          templatesConvertedFromPsd.push(graphic.conversionId);
        }

        hasCustomFont = containsCustomFonts(visibleElements);
        break;
      }

      case 'graphics': {
        // Fetch templates in case something has changed outside of producer
        // Ignoring any hidden graphics in the group
        const templateIds = item.posts.filter((g) => !g.hidden).map((g) => g.template_id);
        if (!templateIds?.length) { break; }

        const templates = await GraphicsService.getMultipleTemplates(uniq(templateIds)).catch((err) => {
          console.error('Failed to fetch graphic templates for analytics logging:', err);
          return null;
        });

        if (!templates) { break; }

        const filteredTemplates = templates
          .filter((t) => templateIds.includes(t._id))
          .reduce((memo, template) => ({
            [template._id]: template,
            ...memo,
          }), {});

        const stats = item.posts.map((post) => {
          const template = filteredTemplates[post.template_id];
          if (!template) {
            return { qrCodeUrls: [], elements: {}, apps: {} };
          }

          const visibleElements = getVisibleEls(
            template.elements,
            post.fields || {},
          );

          qrCodeUrls = visibleElements.filter((el) => el.type === 'qrcode').map((el) => el.permalink);

          // eslint-disable-next-line no-shadow
          const elements = visibleElements.reduce((memo, el) => {
            if (el.type === 'sportradar') {
              sportRadarElements.push({
                id: el.id,
                data: el.data,
              });
            }

            if (el.type === 'qrcode') {
              const qrCodeData = item?.fields?.[el?.data?.provider]?.data;
              if (qrCodeData) {
                qrCodeElements.push({
                  id: el.id,
                  data: qrCodeData,
                });
              }
            }

            return {
              ...memo,
              [`${el.type}_elements`]: (memo[`${el.type}_elements`] || 0) + 1,
            };
          }, {});

          // Partner Apps (e.g. "fox-foresight", "scorestream", "cue")
          // eslint-disable-next-line no-shadow
          const apps = visibleElements.reduce((memo, el) => {
            const { partner } = el;
            if (partner) {
              const key = `${partner}_app_elements`;

              return {
                ...memo,
                [key]: (memo[key] || 0) + 1,
              };
            }

            return memo;
          }, {});

          if (!hasCustomFont) {
            // Keep looking for elements with custom fonts until one is found
            hasCustomFont = containsCustomFonts(visibleElements);
          }

          if (template.conversionId) {
            templatesConvertedFromPsd.push(template.conversionId);
          }

          return { qrCodeUrls, elements, apps };
        });

        [qrCodeUrls, elements, apps] = stats.reduce((memo, graphic) => {
          memo[0].push(...graphic.qrCodeUrls);

          Object.keys(graphic.elements).forEach((key) => {
            // eslint-disable-next-line no-param-reassign
            memo[1][key] = (memo[1][key] || 0) + graphic.elements[key];
          });

          Object.keys(graphic.apps).forEach((key) => {
            // eslint-disable-next-line no-param-reassign
            memo[2][key] = (memo[2][key] || 0) + graphic.apps[key];
          });

          return memo;
        }, [[], {}, {}]);

        break;
      }

      default: break;
    }

    const partialItem = {
      ...item,
      meta: {
        ...item.meta,
      },
    };

    delete partialItem.fields;
    delete partialItem.meta.typeLookup;

    ConduitEventsService.track(
      (preview) ? 'producer.toggled.preview' : 'producer.toggled.live',
      {
        value: event.toLowerCase(), // 'live' | 'off'
        production_id: production._id,
        layout: normalizeType(item.layout),
        type: item.type || item.layout,
        ...elements, // Count of elements by type
        ...apps, // Count of elements by app (partner/tagboard)
        qr_code_urls: JSON.stringify(qrCodeUrls),
        has_custom_font: hasCustomFont,
        owner: production.owner,
        user: user.id,
        converted_graphics: templatesConvertedFromPsd,
        sportRadarElements,
        qrCodeElements,
        item: partialItem,
        connectedScreens: screens?.connectedScreens,
        connectedScreensCount,
        hasConnectedScreens,
      },
      {
        ownerId: rootState.production.production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },
};
